import { Check, Upload, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { h2, p, textStyle } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import Card from '../ui/card';
import './InvisibleCostTracker.css';
import { createUtilityLog } from './invisibleCostApi';

const formatCurrency = (amount) =>
  amount != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount)
    : '—';

const UtilityLogForm = () => {
  // Log screen view
  useScreenLogging('UtilityLogForm', {
    component: 'UtilityLogForm',
    screenType: 'utility-logs',
  });

  const [sites, setSites] = useState([]);
  const [formData, setFormData] = useState({
    utility_type: '',
    site_id: '',
    period: '',
    meter_reading_start: '',
    meter_reading_end: '',
    invoice_amount: '',
    notes: '',
    invoice_file: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Fetch sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const sitesData = await getAllSites();
        setSites(sitesData || []);
      } catch (err) {
        console.error('Error loading sites:', err);
      }
    };
    loadSites();
  }, []);

  // Set default period to current month
  useEffect(() => {
    const today = new Date();
    const period = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    setFormData((prev) => ({ ...prev, period }));
  }, []);

  // Reset form after 3 seconds when success is shown
  useEffect(() => {
    if (!submitSuccess) return;
    const today = new Date();
    const period = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const timer = setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        utility_type: '',
        site_id: '',
        period,
        meter_reading_start: '',
        meter_reading_end: '',
        invoice_amount: '',
        notes: '',
        invoice_file: null,
      });
      setErrors({});
      setSubmitError('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [submitSuccess]);

  // Generate billing period options
  const billingPeriodOptions = useMemo(() => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      options.push({ value: period, label });
    }
    return options;
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, invoice_file: file }));
    }
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, invoice_file: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Utility & site details
    if (!formData.utility_type) newErrors.utility_type = 'Utility type is required';
    if (!formData.site_id) newErrors.site_id = 'Site is required';
    if (!formData.period) newErrors.period = 'Billing period is required';

    // Meter readings
    if (!formData.meter_reading_start) {
      newErrors.meter_reading_start = 'Start reading is required';
    } else if (isNaN(formData.meter_reading_start) || parseFloat(formData.meter_reading_start) < 0) {
      newErrors.meter_reading_start = 'Start reading must be a valid number';
    }
    if (!formData.meter_reading_end) {
      newErrors.meter_reading_end = 'End reading is required';
    } else if (isNaN(formData.meter_reading_end) || parseFloat(formData.meter_reading_end) < 0) {
      newErrors.meter_reading_end = 'End reading must be a valid number';
    }
    if (formData.meter_reading_start && formData.meter_reading_end) {
      const start = parseFloat(formData.meter_reading_start);
      const end = parseFloat(formData.meter_reading_end);
      if (end <= start) {
        newErrors.meter_reading_end = 'End reading must be greater than start reading';
      }
    }

    // Invoice
    if (!formData.invoice_amount) {
      newErrors.invoice_amount = 'Invoice amount is required';
    } else if (isNaN(formData.invoice_amount) || parseFloat(formData.invoice_amount) <= 0) {
      newErrors.invoice_amount = 'Invoice amount must be a valid positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        utility_type: formData.utility_type,
        site_id: formData.site_id,
        period: formData.period,
        meter_reading_start: parseFloat(formData.meter_reading_start),
        meter_reading_end: parseFloat(formData.meter_reading_end),
        invoice_amount: parseFloat(formData.invoice_amount),
        notes: formData.notes || '',
        // In a real implementation, you would upload the file to S3 and include the URL
        invoice_file_url: formData.invoice_file ? 'uploaded-file-url' : null,
      };

      const result = await createUtilityLog(payload);
      
      if (result.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError('Failed to create utility log. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting utility log:', err);
      setSubmitError(err.message || 'Failed to create utility log. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Computed values
  const computedValues = useMemo(() => {
    const start = parseFloat(formData.meter_reading_start) || 0;
    const end = parseFloat(formData.meter_reading_end) || 0;
    const amount = parseFloat(formData.invoice_amount) || 0;
    
    const usage = end > start ? end - start : 0;
    const costPerUnit = usage > 0 ? amount / usage : 0;
    
    return { usage, costPerUnit };
  }, [formData.meter_reading_start, formData.meter_reading_end, formData.invoice_amount]);

  if (submitSuccess) {
    return (
      <div className="utility-log-form">
        <Card variant="light" className="p-8 text-center">
          <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className={textStyle(h2, 'text-gray-900 mb-2')}>Utility Log Created Successfully!</h2>
          <p className={textStyle(p, 'text-gray-600')}>Your utility log has been created successfully.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="utility-log-form">
      <header className="utility-log-form-header">
        <h1 className={textStyle(h2, 'text-gray-900')}>Create Utility Log</h1>
        <p className={textStyle(p, 'text-gray-600 mt-2')}>
          Track utility consumption and costs across your sites
        </p>
      </header>

      <form onSubmit={handleSubmit}>
        {/* Utility Type, Site, Period */}
        <Card variant="light" className="utility-log-form-section p-6">
          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Utility Type *</label>
            <select
              value={formData.utility_type}
              onChange={(e) => handleInputChange('utility_type', e.target.value)}
              className="utility-log-form-select"
            >
              <option value="">Select utility type</option>
              <option value="Electricity">Electricity</option>
              <option value="Water">Water</option>
              <option value="Gas">Gas</option>
              <option value="Internet">Internet</option>
              <option value="Other">Other</option>
            </select>
            {errors.utility_type && (
              <span className="utility-log-form-error">{errors.utility_type}</span>
            )}
          </div>

          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Site *</label>
            <select
              value={formData.site_id}
              onChange={(e) => handleInputChange('site_id', e.target.value)}
              className="utility-log-form-select"
            >
              <option value="">Select site</option>
              {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
            </select>
            {errors.site_id && <span className="utility-log-form-error">{errors.site_id}</span>}
          </div>

          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Billing Period *</label>
            <select
              value={formData.period}
              onChange={(e) => handleInputChange('period', e.target.value)}
              className="utility-log-form-select"
            >
              <option value="">Select billing period</option>
              {billingPeriodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.period && <span className="utility-log-form-error">{errors.period}</span>}
          </div>
        </Card>

        {/* Meter Readings */}
        <Card variant="light" className="utility-log-form-section p-6">
          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Meter Reading Start *</label>
            <input
              type="number"
              step="0.01"
              value={formData.meter_reading_start}
              onChange={(e) => handleInputChange('meter_reading_start', e.target.value)}
              className="utility-log-form-input"
              placeholder="Enter starting meter reading"
            />
            {errors.meter_reading_start && (
              <span className="utility-log-form-error">{errors.meter_reading_start}</span>
            )}
          </div>

          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Meter Reading End *</label>
            <input
              type="number"
              step="0.01"
              value={formData.meter_reading_end}
              onChange={(e) => handleInputChange('meter_reading_end', e.target.value)}
              className="utility-log-form-input"
              placeholder="Enter ending meter reading"
            />
            {errors.meter_reading_end && (
              <span className="utility-log-form-error">{errors.meter_reading_end}</span>
            )}
          </div>

          {formData.meter_reading_start && formData.meter_reading_end && (
            <div className="utility-log-computed-values">
              <h4>Computed Usage</h4>
              <div className="utility-log-computed-row">
                <span>Usage:</span>
                <span className="font-semibold">{computedValues.usage.toLocaleString('en-IN')} units</span>
              </div>
            </div>
          )}
        </Card>

        {/* Invoice & Notes */}
        <Card variant="light" className="utility-log-form-section p-6">
          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Invoice Amount (₹) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.invoice_amount}
              onChange={(e) => handleInputChange('invoice_amount', e.target.value)}
              className="utility-log-form-input"
              placeholder="Enter invoice amount"
            />
            {errors.invoice_amount && (
              <span className="utility-log-form-error">{errors.invoice_amount}</span>
            )}
          </div>

          <div className="utility-log-form-group">
            <label className="utility-log-form-label">Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="utility-log-form-textarea"
              placeholder="Add any additional notes or comments"
              rows={4}
            />
          </div>

          <div className="utility-log-form-group">
            <label className="utility-log-form-label" htmlFor="invoice-file-input">
              Invoice File (Optional)
            </label>
            {!formData.invoice_file ? (
              <label htmlFor="invoice-file-input" className="utility-log-file-upload">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <input
                  id="invoice-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <p className="text-sm text-gray-600">Click to upload invoice file</p>
                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG up to 10MB</p>
              </label>
            ) : (
              <div className="utility-log-file-preview">
                <span className="text-sm text-gray-700">{formData.invoice_file.name}</span>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-red-600 hover:text-red-800"
                  aria-label="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {formData.meter_reading_start &&
            formData.meter_reading_end &&
            formData.invoice_amount && (
              <div className="utility-log-computed-values">
                <h4>Computed Values</h4>
                <div className="utility-log-computed-row">
                  <span>Usage:</span>
                  <span className="font-semibold">{computedValues.usage.toLocaleString('en-IN')} units</span>
                </div>
                <div className="utility-log-computed-row">
                  <span>Cost per Unit:</span>
                  <span className="font-semibold">{formatCurrency(computedValues.costPerUnit)}</span>
                </div>
              </div>
            )}

          {submitError && (
            <div className="utility-log-form-error mb-4 p-3 bg-red-50 border border-red-200 rounded">
              {submitError}
            </div>
          )}
        </Card>

        <div className="utility-log-form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="utility-log-form-button utility-log-form-button-primary"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UtilityLogForm;
